<template>
  <b-form @submit.stop.prevent="submit()">
    <b-form-group :label="getLabel('START_DATE')" label-class="mr-2">
      <datepicker
        v-model="range.start"
        input-class="form-control datepicker-input"
        :disabled-dates="disabledDates"
        @input="notify()"
      />
    </b-form-group>
    <b-form-group :label="getLabel('END_DATE')" label-class="mr-2">
      <datepicker
        v-model="range.end"
        input-class="form-control datepicker-input"
        :disabled-dates="disabledDates"
        @input="notify()"
      />
    </b-form-group>
    <b-form-group :label="getLabel('TIMEZONE')" label-class="mr-2">
      <b-input-group class="mb-3">
        <b-form-select
          v-model="range.timezone"
          class="form-control"
          @input="notify()"
        >
          <b-form-select-option
            v-for="(timezone, index) in timezones"
            :key="index"
            :value="timezone.offset"
          >
            {{ timezone.label }}
          </b-form-select-option>
        </b-form-select>
      </b-input-group>
    </b-form-group>
    <b-form-group v-if="!hideRefresh">
      <b-button block variant="outline-primary" type="submit">
        <i class="fas fa-redo" />&nbsp;
        <span v-label-key="'REFRESH'">Refresh</span>
      </b-button>
    </b-form-group>
  </b-form>
</template>
<script>
import $time from '@/services/time';
import Datepicker from 'vuejs-datepicker';
import { mapGetters } from 'vuex';

export default {
  name: 'DateRangeSelector',
  components: {
    Datepicker,
  },
  props: {
    hideRefresh: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('translations', {
      getLabel: 'getLabel',
    }),
    disabledDates() {
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 366);
      return {
        from: maxDate,
      };
    },
  },
  data() {
    return {
      range: {},
      timezones: $time.timezones,
    };
  },
  created() {
    // Sets the default start date to the beginning of the day 6 days ago
    this.range.start =
      $time.latestStartDate ||
      new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 6);
    this.range.start.setHours(0, 0, 0, 0);

    // Sets the default start date to the end of today. This provides a 6.x day default time range.
    this.range.end = $time.latestEndDate || new Date();
    this.range.end.setHours(23, 59, 59, 0);

    // Selects the user's current timezone by default
    this.range.timezone = this.timezones.filter(
      (tz) => tz.offset === `${new Date().getTimezoneOffset()}`,
    )[0].offset;

    // Notifies parent components so they can render default data
    this.notify();
  },
  methods: {
    notify() {
      // Dispatches an input event using UNIX timestamps and expressing timezone in terms of hours
      const standardizedRange = {};
      standardizedRange.start = $time.getTimestamp(
        this.range.start,
        this.range.timezone,
      );
      standardizedRange.end = $time.getTimestamp(
        this.range.end,
        this.range.timezone,
      );
      standardizedRange.timezone = $time.formatHours(this.range.timezone);
      standardizedRange.dayCount = Math.ceil(
        (this.range.end.getTime() - this.range.start.getTime()) /
          (1000 * 60 * 60 * 24),
      );
      this.$emit('input', standardizedRange);

      $time.latestStartDate = this.range.start;
      $time.latestEndDate = this.range.end;
    },
    submit() {
      this.notify();
      this.$emit('refresh');
    },
  },
};
</script>
<style scoped>
datepicker input {
  border-style: none !important;
}
</style>
<style>
/* This only seems to work when it isn't scoped.
 * The datepicker-input class name is determined by the datepicker sub-components used in this component.
 */
.datepicker-input {
  background-color: #fff !important;
}
</style>
